/**
 * SplitBar: share of a whole in two to four parts (table vs to-go, payment methods). Legend shows value and percent.
 * Props: caption, parts [{ key, label, value }] (first 4 used), formatValue (default: the number), labelHeading?,
 *        valueLabel? (table header, default "Value"), loading?, className?.
 */
import { useTranslation } from 'react-i18next';
import { StackedBar } from './StackedBar';

export interface SplitPart {
    key: string;
    label: string;
    value: number;
}

export interface SplitBarProps {
    caption: string;
    parts: SplitPart[];
    formatValue?: (n: number) => string;
    labelHeading?: string;
    valueLabel?: string;
    loading?: boolean;
    className?: string;
}

export const SplitBar = ({ caption, parts, formatValue = String, labelHeading, valueLabel, loading, className }: SplitBarProps) => {
    const { t, i18n } = useTranslation('charts');
    const used = parts.slice(0, 4);
    const total = used.reduce((s, p) => s + Math.max(p.value, 0), 0);
    const pct = (v: number) => new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 0 }).format(total > 0 ? Math.max(v, 0) / total : 0);
    return (
        <StackedBar
            caption={caption}
            loading={loading}
            className={className}
            parts={used.map((p) => ({ ...p, text: `${formatValue(p.value)} (${pct(p.value)})` }))}
            headers={[labelHeading ?? t('item'), valueLabel ?? t('value'), t('share')]}
            rows={used.map((p) => [p.label, formatValue(p.value), pct(p.value)])}
        />
    );
};
